import { useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef, GridApi } from 'ag-grid-community';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '../../shared/api';
import { CohortDescriptions } from '@census/shared';
import { Icon } from '../../ui/Icon';
import { cohortNameOrCode, useCohorts } from '../../shared/cohorts';

export function ClientsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const gridRef = useRef<AgGridReact | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('ACTIVE');
  const [cohortFilter, setCohortFilter] = useState<string>('');
  const [search, setSearch] = useState('');
  const [rebuildingId, setRebuildingId] = useState<string | null>(null);
  const { byCode: cohortByCode } = useCohorts();

  const { data: clients, isLoading } = useQuery({
    queryKey: ['clients', statusFilter, cohortFilter],
    queryFn: () =>
      api
        .get('/clients', {
          params: { status: statusFilter || undefined, cohort: cohortFilter || undefined },
        })
        .then((r) => r.data),
  });

  const { data: caseManagers } = useQuery({
    queryKey: ['case-manager-resources'],
    queryFn: () =>
      api
        .get('/resources', { params: { type: 'COMMUNITY_PROVIDER', activeOnly: 'true' } })
        .then((r) => r.data),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      api.patch(`/clients/${id}`, patch).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast.success('Client updated');
    },
  });

  const discharge = useMutation({
    mutationFn: (id: string) => api.post(`/clients/${id}/discharge`, {}).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast.success('Client discharged');
    },
  });

  const reactivate = useMutation({
    mutationFn: (id: string) => api.post(`/clients/${id}/reactivate`, {}).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast.success('Client reactivated');
    },
  });

  const caseManagerValues = useMemo(
    () => ['', ...(caseManagers ?? []).map((c: any) => c.id)],
    [caseManagers],
  );

  const columnDefs = useMemo<ColDef[]>(
    () => [
      { headerName: 'First name', field: 'firstName', editable: true, width: 130 },
      { headerName: 'Last name', field: 'lastName', editable: true, width: 130 },
      { headerName: 'Phone', field: 'cellPhone', editable: true, width: 150 },
      { headerName: 'Location', valueGetter: (p) => p.data?.locationCity ?? 'N/A', width: 130 },
      {
        headerName: 'Group',
        field: 'cohortId',
        width: 90,
        cellClass: 'font-mono font-semibold text-brand-700',
      },
      {
        headerName: 'Program',
        valueGetter: (p) =>
          p.data?.cohort
            ? `${p.data.cohort} — ${cohortNameOrCode(p.data.cohort, cohortByCode)}`
            : '',
        width: 200,
      },
      {
        headerName: 'Case manager',
        field: 'caseManagerResourceId',
        valueFormatter: (p) =>
          (caseManagers ?? []).find((c: any) => c.id === p.value)?.displayName ?? '—',
        editable: true,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: caseManagerValues },
        width: 170,
      },
      { headerName: 'EMR ID', field: 'externalRef', editable: true, width: 140 },
      {
        headerName: 'Status',
        field: 'status',
        width: 120,
        editable: true,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: ['ACTIVE', 'ON_HOLD', 'DISCHARGED'] },
        cellRenderer: (p: any) => {
          const cls =
            p.value === 'ACTIVE'
              ? 'pill-success'
              : p.value === 'ON_HOLD'
                ? 'pill-warn'
                : 'pill-muted';
          return <span className={cls}>{p.value}</span>;
        },
      },
      { headerName: 'Telehealth', field: 'telehealthAllowed', editable: true, cellEditor: 'agCheckboxCellEditor', width: 110 },
      { headerName: 'Home/Comm', field: 'homeCommunityOk', editable: true, cellEditor: 'agCheckboxCellEditor', width: 120 },
      { headerName: 'IOP Track', field: 'iopTrackName', editable: false, width: 180, valueGetter: (p: any) => p.data?.iopTrackName ?? '' },
      { headerName: 'Nurs.', field: 'nursingGroupDay', editable: true, cellEditor: 'agSelectCellEditor', cellEditorParams: { values: ['', 'MON', 'TUE', 'WED', 'THU', 'FRI'] }, width: 90 },
      {
        headerName: 'Required',
        valueGetter: (p) => (p.data?.requiredServices || []).join(', '),
        width: 240,
      },
      { headerName: 'Intake', field: 'intakeDate', width: 110 },
      {
        headerName: 'Actions',
        width: 280,
        pinned: 'right',
        sortable: false,
        cellRenderer: (p: any) => (
          <div className="flex h-full items-center gap-3 text-xs">
            <Link className="text-brand-700 hover:underline" to={`/clients/${p.data.id}`}>
              View
            </Link>
            <Link
              className="text-brand-700 hover:underline"
              to={`/print/client/${p.data.id}`}
              target="_blank"
            >
              Print
            </Link>
            <button
              className="text-amber-700 hover:underline"
              disabled={rebuildingId === p.data.id}
              onClick={() => {
                if (
                  !confirm(
                    'Re-run scheduling for this client? Existing scheduled appointments will be replaced.',
                  )
                )
                  return;
                setRebuildingId(p.data.id);
                api.post(`/scheduling/rebuild/${p.data.id}`)
                  .then(() => {
                    toast.success('Re-scheduled');
                    queryClient.invalidateQueries({ queryKey: ['clients'] });
                  })
                  .catch(() => {
                    queryClient.invalidateQueries({ queryKey: ['clients'] });
                  })
                  .finally(() => setRebuildingId(null));
              }}
            >
              Rebuild
            </button>
            {p.data.status === 'DISCHARGED' ? (
              <button
                className="text-emerald-700 hover:underline"
                onClick={() => reactivate.mutate(p.data.id)}
              >
                Reactivate
              </button>
            ) : (
              <button
                className="text-rose-600 hover:underline"
                onClick={() => {
                  if (
                    confirm(
                      `Discharge ${p.data.firstName} ${p.data.lastName}? Future SCHEDULED instances will be canceled.`,
                    )
                  ) {
                    discharge.mutate(p.data.id);
                  }
                }}
              >
                Discharge
              </button>
            )}
          </div>
        ),
      },
    ],
    [queryClient, caseManagers, caseManagerValues, discharge, reactivate, cohortByCode],
  );

  function onCellValueChanged(e: any) {
    const id = e.data.id as string;
    const patch: Record<string, unknown> = { [e.colDef.field]: e.newValue };
    if (e.colDef.field === 'iopTrackResourceId' && e.newValue === '') patch.iopTrackResourceId = null;
    if (e.colDef.field === 'nursingGroupDay' && e.newValue === '') patch.nursingGroupDay = null;
    if (e.colDef.field === 'caseManagerResourceId' && e.newValue === '')
      patch.caseManagerResourceId = null;
    if (e.colDef.field === 'externalRef' && e.newValue === '') patch.externalRef = null;
    updateMutation.mutate({ id, patch });
  }

  function onGridReady(params: { api: GridApi }) {
    params.api.sizeColumnsToFit();
  }

  function exportCsv() {
    gridRef.current?.api.exportDataAsCsv({
      fileName: `clients-${new Date().toISOString().slice(0, 10)}.csv`,
    });
  }

  return (
    <div className="flex h-full flex-col gap-4">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-ink sm:text-2xl">Clients</h1>
          <p className="text-sm text-ink-subtle">
            Inline-edit any cell. Cohort ID is assigned at intake; status changes are soft-delete.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="label">Status</label>
            <select
              className="input h-9 py-1.5"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="ACTIVE">Active</option>
              <option value="ON_HOLD">On Hold</option>
              <option value="DISCHARGED">Discharged</option>
              <option value="">All</option>
            </select>
          </div>
          <div>
            <label className="label">Cohort</label>
            <select
              className="input h-9 py-1.5"
              value={cohortFilter}
              onChange={(e) => setCohortFilter(e.target.value)}
            >
              <option value="">All</option>
              {['A', 'B', 'C', 'D', 'E', 'F', 'G'].map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <button className="btn-secondary h-9 whitespace-nowrap" onClick={exportCsv}>
            Export CSV
          </button>
          <button
            className="btn-primary h-9 whitespace-nowrap"
            onClick={() => navigate('/intake')}
          >
            <Icon name="user-plus" size={14} /> New intake
          </button>
        </div>
      </header>

      <div className="flex items-center gap-2">
        <div className="relative w-full max-w-xs">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted">
            <Icon name="users" size={14} />
          </span>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, cohort, program…"
            className="h-9 w-full rounded-md border border-line bg-white pl-9 pr-3 text-sm outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
          />
        </div>
      </div>

      <div className="ag-theme-quartz flex-1 overflow-hidden rounded-card shadow-card ring-1 ring-line">
        <AgGridReact
          ref={gridRef}
          rowData={clients ?? []}
          columnDefs={columnDefs}
          animateRows
          quickFilterText={search}
          singleClickEdit
          stopEditingWhenCellsLoseFocus
          onGridReady={onGridReady}
          onCellValueChanged={onCellValueChanged}
          overlayLoadingTemplate={isLoading ? 'Loading clients…' : ''}
        />
      </div>
    </div>
  );
}
